import { FACILITY_MAP } from '../constants/inventoryConstants';
import { PATH_DASHBOARD } from '../routes/paths';

/**
 * Dark Mode facilities are defined in FACILITY_MAP (IDs 13 / 14).
 * Matches the existing project checks used in Sales/POS/Inventory.
 */
export function isDarkModeFacility(facilityId) {
  const id = String(facilityId ?? '');
  return id === String(FACILITY_MAP.DARK_MAHATTA) || id === String(FACILITY_MAP.DARK_QASMIA);
}

/** VAT reports that must NOT be available in Dark Mode facilities. */
export const DARK_MODE_RESTRICTED_VAT_PATHS = [
  PATH_DASHBOARD.reports.vatsummary,
  PATH_DASHBOARD.reports.outputvat,
  PATH_DASHBOARD.reports.inputvat,
  PATH_DASHBOARD.reports.vatreconciliation
];

export function isDarkModeRestrictedVatPath(pathname = '') {
  return DARK_MODE_RESTRICTED_VAT_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

export function getDarkModeVatFallbackPath() {
  return PATH_DASHBOARD.reports.creditnotesrefunds;
}

const CREDIT_NOTES_SIDEBAR_ITEM = {
  title: 'Credit Notes & Refunds Report',
  path: PATH_DASHBOARD.reports.creditnotesrefunds,
  permissionId: 155
};

/**
 * Dark Mode facility sidebar rules for VAT-related reports:
 * - hide Summary / Output / Input / Reconciliation
 * - promote Credit Notes & Refunds to a direct child under Reports
 * - remove the empty VAT Reports group
 */
export function filterSidebarForFacility(navConfig, facilityId) {
  if (!isDarkModeFacility(facilityId)) return navConfig;

  const creditNotesPath = PATH_DASHBOARD.reports.creditnotesrefunds;

  const transformItems = (items = [], { underReports = false } = {}) => {
    const next = [];

    (items || []).forEach((item) => {
      if (isDarkModeRestrictedVatPath(item.path || '')) return;

      const isVatReportsGroup = String(item.title || '').toLowerCase() === 'vat reports';
      if (isVatReportsGroup) {
        // Drop VAT Reports entirely in dark mode; Credit Notes is promoted separately.
        return;
      }

      if (item.children?.length) {
        const isReportsRoot =
          underReports === false &&
          (String(item.title || '').toLowerCase() === 'reports' ||
            item.path === PATH_DASHBOARD.reports.root);

        const children = transformItems(item.children, { underReports: isReportsRoot || underReports });

        if (isReportsRoot) {
          const withoutCreditNotes = children.filter((child) => child.path !== creditNotesPath);
          next.push({
            ...item,
            children: [...withoutCreditNotes, { ...CREDIT_NOTES_SIDEBAR_ITEM }]
          });
          return;
        }

        if (!children.length) return;
        next.push({ ...item, children });
        return;
      }

      next.push(item);
    });

    return next;
  };

  return (navConfig || []).map((group) => ({
    ...group,
    items: transformItems(group.items || [])
  }));
}
